import React from 'react'

import { DateTime } from 'luxon'

import { Alert, SeverityPill, SimpleTable, TableSkeleton } from '@/components/ui'
import { useApi, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'

interface AlertAcknowledgementByAssetParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  severity?: number[]
  alertConfigIds?: string
}

interface ApiRow {
  alertConfigName: string
  alertConfigId: string
  raisedTime: string
  clearedTime: string | null
  severityCode: string
  user: string | null
  userId: number | null
  action: string | null
  location: string | null
  note: string | null
  actionCreatedAt: string | null
  mpName: string | null
}

interface AlertAcknowledgementRow {
  alertConfigName: string
  alertConfigId: string
  raisedTime: Date
  clearedTime: Date | null
  severityCode: string
  user: string | null
  userId: number | null
  action: string | null
  location: string | null
  note: string | null
  actionCreatedAt: Date | null
  mpName: string | null
}

const AlertAcknowledgementByAsset: InsightDefinition<AlertAcknowledgementByAssetParams, AlertAcknowledgementRow[]> = {
  active: true,
  slug: 'asset/alert-action-history',
  category: 'asset',
  name: 'Alert Acknowledgement By Asset',
  description: 'Alerts raised on an asset and the actions taken on them',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'severity') return { ...parameters, alertConfigIds: undefined }
      if (field === 'alertConfigIds') return { ...parameters, severity: undefined }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alert Name' },
      { id: 'alertConfigIds', control: 'AlertNameDropdown', required: false },
      { id: 'severity', control: 'SeverityDropdown' },

      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptions.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
    ],

    isValid: (configuration: AlertAcknowledgementByAssetParams) => {
      const hasSeverity = configuration.severity && configuration.severity.length > 0
      const hasAlert = configuration.alertConfigIds && configuration.alertConfigIds.length > 0

      if (!configuration.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }
      if (configuration.isDrilldown) {
        return true
      }
      if (hasSeverity === hasAlert) {
        if (!hasSeverity && !hasAlert) return 'Alert Name or Severity is required'
        return 'Choose either Alert Name or Severity'
      }
      return true
    },
  },

  load: async (api: ApiService, parameters: AlertAcknowledgementByAssetParams) => {
    const focusTime =
      typeof (parameters as { from?: string }).from === 'string'
        ? (parameters as { from: string }).from
        : new Date().toISOString()
    const query = buildQuery({ ...parameters, focusTime })
    const response = await api.get<ApiRow[]>({
      path: `/alerts/acknowledgement/history?${query}`,
      origin: 'AlertActionHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data || response.data.length === 0) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data.map((row) => ({
      ...row,
      raisedTime: new Date(row.raisedTime),
      clearedTime: row.clearedTime ? new Date(row.clearedTime) : null,
      actionCreatedAt: row.actionCreatedAt ? new Date(row.actionCreatedAt) : null,
    }))
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={10} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const format = 'dd-MMM-yyyy hh:mm a'
    const body = data.map((row, index) => ({
      'Alert Name': row.alertConfigName,
      'Monitoring Point': row.mpName ?? '-',
      Severity: {
        value: Number(row.severityCode),
        display: <SeverityPill label={getSeverityLabel(row.severityCode)} severityCode={Number(row.severityCode)} />,
      },

      'Raised Time': {
        id: `raised-${row.alertConfigId}-${index}-${row.raisedTime.getTime()}`,
        date: row.raisedTime,
        display: DateTime.fromJSDate(row.raisedTime).toFormat(format),
      },
      'Cleared Time': row.clearedTime
        ? {
            id: `cleared-${row.alertConfigId}-${index}-${row.clearedTime.getTime()}`,
            date: row.clearedTime,
            display: DateTime.fromJSDate(row.clearedTime).toFormat(format),
          }
        : '-',
      'Raised Location': row.location ?? '-',
      'Action Created At': row.actionCreatedAt
        ? {
            id: `action-${row.alertConfigId}-${index}-${row.actionCreatedAt.getTime()}`,
            date: row.actionCreatedAt,
            display: DateTime.fromJSDate(row.actionCreatedAt).toFormat(format),
          }
        : '-',
      User: row.user ?? '-',
      Action: row.action ?? '-',
      Note: row.note ?? '-',
    }))

    const dataItem = body[0]
    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(10)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as AlertAcknowledgementByAssetParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'AlertActionHistory#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default AlertAcknowledgementByAsset
